import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, Crown, Zap, Building, Gift } from "lucide-react";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import TopBar from "../components/TopBar";
import { PLANS, LEGAL_TEXT } from "../config/pricingPolicy";

const ICONS = {
  free: Gift,
  creator: Zap,
  pro: Crown,
  business: Building,
  agency: Building,
  founding: Crown,
};

export default function PricingPage() {
  const [providerInfo, setProviderInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function loadRealProducts() {
      try {
        const providerRes = await api.get("/billing/provider").then(r => r.data).catch(() => null);
        if (!cancelled && providerRes) setProviderInfo(providerRes);
      } catch {}
    }
    loadRealProducts();
    return () => { cancelled = true; };
  }, []);

  return (
    <AppShell>
      <TopBar title="Pricing" subtitle={`${providerInfo?.provider || "Dodo"} · ${PLANS.length} plans · stock assembly + FFmpeg`} />

      <div className="p-8 space-y-8">
        {/* How it works */}
        <section className="max-w-5xl mx-auto border border-zinc-800 bg-[#121212] rounded-sm p-6 space-y-3">
          <h2 className="font-semibold">How it works</h2>
          <ol className="space-y-2 text-sm text-zinc-300">
            <li>1. You describe what video should explain</li>
            <li>2. We search Pexels, Unsplash, Pixabay for licensed footage matching your topic</li>
            <li>3. We add AI script + Gemini/ElevenLabs voiceover + captions and export via FFmpeg</li>
          </ol>
          <p className="text-xs text-zinc-500">Drafting is free, you pay only for finished export. No fake AI footage.</p>
        </section>

        {/* Legal text */}
        <div className="max-w-5xl mx-auto">
          <p className="text-xs text-zinc-500">{LEGAL_TEXT}</p>
        </div>

        {/* Plans */}
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PLANS.map((p) => {
            const Icon = ICONS[p.key] || Zap;
            const isFounding = p.once;
            const cardClass = isFounding
              ? "rounded-sm p-6 space-y-4 flex flex-col bg-[#111]"
              : `border rounded-sm p-6 space-y-4 flex flex-col ${p.hero ? "border-[#00E5FF] bg-[#00E5FF]/5 shadow-lg" : "border-zinc-800 bg-[#121212] text-white"}`;
            const cardStyle = isFounding ? { border: "2px solid #FFD700" } : undefined;
            return (
              <div key={p.key} data-testid={`pricing-card-${p.key}`} className={cardClass} style={cardStyle}>
                <div className="flex items-center gap-2">
                  <Icon size={18} className={p.hero ? "text-[#00E5FF]" : isFounding ? "text-[#FFD700]" : "text-zinc-400"} strokeWidth={1.5} />
                  <h3 className={`font-semibold ${isFounding ? "text-white" : ""}`}>{p.name}</h3>
                  {p.badge && (
                    <span className={`ml-auto text-[9px] uppercase px-1.5 py-0.5 rounded-sm ${isFounding ? "bg-[#FFD700] text-black" : p.hero ? "bg-[#00E5FF] text-black" : "bg-zinc-800 text-zinc-300"}`}>
                      {p.badge}
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className={`text-3xl font-bold ${isFounding ? "text-white" : ""}`}>{p.price}</span>
                  <span className="text-xs text-zinc-500">{p.priceSuffix}</span>
                  {p.priceNote && <span className="text-xs text-zinc-500">{p.priceNote}</span>}
                </div>
                <div className="text-xs text-zinc-400">{p.desc}</div>
                {p.highlight && <div className="font-mono text-[11px] text-[#00FF66]">{p.highlight}</div>}
                <ul className="space-y-2 pt-2 border-t border-zinc-800 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-xs text-zinc-300">
                      <Check size={12} className="text-[#00FF66]" strokeWidth={2} /> {f}
                    </li>
                  ))}
                </ul>
                {p.checkoutUrl ? (
                  <a
                    data-testid={`pricing-choose-${p.key}`}
                    href={p.checkoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-widest px-4 py-2 rounded-sm transition-colors ${isFounding ? "bg-[#FFD700] text-black hover:bg-[#FFE033]" : p.hero ? "bg-[#00E5FF] text-black hover:bg-[#33EFFF]" : "border border-zinc-700 text-white hover:border-[#00E5FF] hover:text-[#00E5FF]"}`}
                  >
                    {isFounding ? "Claim founding" : `Choose ${p.name}`}
                  </a>
                ) : (
                  <Link
                    data-testid={`pricing-choose-${p.key}`}
                    to="/app/projects/new"
                    className="w-full flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-widest px-4 py-2 rounded-sm border border-zinc-700 text-gray-900 dark:text-white hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors"
                  >
                    Get started
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        <div className="max-w-5xl mx-auto">
          <p className="text-xs text-zinc-500">Prices shown are in AUD and include 10% Australian GST. Dodo calculates the applicable tax for your billing country at checkout.</p>
        </div>

        <div className="max-w-5xl mx-auto text-center">
          <Link to="/app/projects" className="text-xs text-zinc-500 hover:text-gray-900 dark:text-white">← Back to projects</Link>
        </div>
      </div>
    </AppShell>
  );
}
